/**
 * The join link, both directions.
 *
 * A created room presents its code with a link an opponent can open with one
 * tap. The link is a **path**: `https://host/join/PQ4T`. It has to be,
 * because the Versus tab's code boxes (the shell's `CodeBoxes`) keep the last
 * path segment of whatever is pasted into them and drop a fragment, so a
 * pasted path link fills the code in and a pasted hash link would not.
 *
 * A path route costs nothing on the server: its static handler answers any
 * path without a file extension with the app shell
 * (`apps/server/src/http/static.ts`), and the service worker's navigation
 * fallback does the same offline. The app reads the path once at load and
 * replaces it with `/` (`App.svelte`), so an installed app whose `start_url`
 * is `/` is unaffected.
 *
 * The older hash form, `https://host/#/join/PQ4T`, is still read: links
 * already sent keep working.
 *
 * Parsing is as forgiving as the join box: the code goes through
 * `normaliseRoomCode`, so a link a chat client lower-cased, or one retyped
 * with an `O` for a `Q`, still lands in the right room. A link naming
 * something that is not a code returns `null` rather than a room nobody has.
 */
import { isRoomCode, normaliseRoomCode } from '@interlock/protocol';

/** The path a join link carries, up to the code. */
export const JOIN_PATH_PREFIX = '/join/';

/** The hash prefix of the older link form. Read, never written. */
export const JOIN_HASH_PREFIX = '#/join/';

/**
 * The link to send an opponent.
 *
 * `origin` rather than `href`: the page may itself have been opened on a join
 * link, and a share link built from that would put one route inside another.
 */
export function joinLink(code: string, origin: string): string {
  return `${origin.replace(/\/+$/, '')}${JOIN_PATH_PREFIX}${code}`;
}

/** The code a route's remainder names, or `null`: the first segment, decoded and normalised. */
function codeFrom(rest: string): string | null {
  const raw = rest.split(/[?#/]/)[0] ?? '';
  if (raw === '') return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return null;
  }
  const code = normaliseRoomCode(decoded);
  return isRoomCode(code) ? code : null;
}

/** The room code a URL path names (`/join/PQ4T`), or `null`. */
export function parseJoinPath(pathname: string): string | null {
  if (!pathname.startsWith(JOIN_PATH_PREFIX)) return null;
  return codeFrom(pathname.slice(JOIN_PATH_PREFIX.length));
}

/**
 * The room code a URL hash names (`#/join/PQ4T`), or `null`.
 *
 * Accepts the hash with or without its leading `#`, because `location.hash`
 * has one and a hand-written test usually does not.
 */
export function parseJoinHash(hash: string): string | null {
  const withHash = hash.startsWith('#') ? hash : `#${hash}`;
  if (!withHash.startsWith(JOIN_HASH_PREFIX)) return null;
  return codeFrom(withHash.slice(JOIN_HASH_PREFIX.length));
}

/** The room code the page was opened on, in either form; the path wins. */
export function parseJoinLocation(location: {
  readonly pathname: string;
  readonly hash: string;
}): string | null {
  return parseJoinPath(location.pathname) ?? parseJoinHash(location.hash);
}

/**
 * The room code a pasted join link names, in either form, or `null` when the
 * text is not a join link. For the code boxes: a pasted link of the older
 * hash form has no code in its path, so the boxes cannot find it themselves.
 */
export function codeFromJoinLink(text: string): string | null {
  let url: URL;
  try {
    url = new URL(text.trim());
  } catch {
    return null;
  }
  return parseJoinLocation(url);
}

/** Whether a path is a join route at all, with a usable code or not: one the app replaces with `/`. */
export function isJoinPath(pathname: string): boolean {
  return pathname === '/join' || pathname.startsWith(JOIN_PATH_PREFIX);
}
